import { useHistory } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { SocietyClientDataForm, SocietyClientFilters } from '../../app/models/SocietyClient';

import DefaultCard from '../../components/default/DefaultCard';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';

import { ServiceResponse } from '../../app/services/shared/interfaces';
import useSocietyClientService from '../../app/services/hooks/useSocietyClientService';
import DefaultModal from '../../components/default/DefaultModal';
import ClientCreate from './components/ClientCreate';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import ClientDataTable from './components/tables/ClientDataTable';
import { toast } from 'react-toastify';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Clientes',
        url: '/clients',
        isActive: true
    }
];

const Client = () => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const history = useHistory();
    const { 
        fetchingGetSocietyClients, 
        getSocietyClients, 
        deleteSocietyClient, 
        activeSocietyClient 
    } = useSocietyClientService();

    const { auth } = useContext(AuthContext);

    const [societyClients, setSocietyClients] = useState<SocietyClientDataForm[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const { navigationPage } = useNavigationPage();

    const [showingCreate, setShowingCreate] = useState(false);

    const [filter, setFilter] = useState<SocietyClientFilters>({
        society_id: undefined,
        rut: '',
        business_name: '',
        name: '',
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order, filter.society_id]);

    const reloadTable = () => {
        getSocietyClients(filter, {
            onSuccess: (response: ServiceResponse) => {
                setSocietyClients(response.data.society_clients || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (societyClientId: number) => {
        // Ir a una vista dedicada de edición (similar a proveedores)
        navigationPage(`/clients/${societyClientId}/edit`);
    };

    const active = (societyClientId: number, active: boolean) => {
        activeSocietyClient(societyClientId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    const destroy = (societyClientId: number) => {
        const _text = 'Está a punto de eliminar el cliente de la sociedad #' + societyClientId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando cliente...');
                        deleteSocietyClient(societyClientId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El cliente no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Clientes" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo cliente" />}
                />

                <ClientDataTable
                    active={active}
                    clients={societyClients.map(sc => ({
                        id: sc.id,
                        rut: sc.client?.rut || '',
                        business_name: sc.client?.business_name || '',
                        address: sc.client?.address || '',
                        country: sc.client?.country || '',
                        first_name_contact: sc.client?.first_name_contact || '',
                        last_name_contact: sc.client?.last_name_contact || '',
                        second_last_name_contact: sc.client?.second_last_name_contact || '',
                        email_contact: sc.client?.email_contact || '',
                        phone_code_contact: sc.client?.phone_code_contact || '',
                        phone_contact: sc.client?.phone_contact || '',
                        is_active: sc.is_active ?? true,
                        society: sc.society
                    }))}
                    totalRows={totalRows}
                    loading={fetchingGetSocietyClients}
                    destroy={destroy}
                    edit={showEdit}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>
            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear cliente"
                    backdrop={true}
                    showFooter={false}
                    size="lg"
                >
                    <ClientCreate
                        onSaved={(societyId?: number) => {
                            hideCreate();
                            // Si se creó un cliente, actualizar el filtro con la sociedad si es necesario
                            if (societyId && !filter.society_id) {
                                setFilter({
                                    ...filter,
                                    society_id: societyId,
                                    page: 1
                                });
                            } else {
                                reloadTable();
                            }
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

        </>
    );
};

export default Client;
